import { getChainConfigEntry } from '@/config/chain'
import { formatPercentage } from '@/domain/common/format'
import { sortByAPY, sortByUsdValue } from '@/domain/common/sorters'
import { EModeCategoryId } from '@/domain/e-mode/types'
import { OpenDialogFunction } from '@/domain/hooks/useDialog'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { applyExtraIncentivesUSDCToBorrowAPY, useExtraIncentivesUSDC } from '@/domain/hooks/useExtraIncentivesUSDC'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { useCardSolverConfigs, useStoreHydrated } from '@/domain/state'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { CardRouteBadge } from '@/features/card/components/CardRouteBadge'
import { getPooledBorrowCardRouteBadge } from '@/features/card/utils/cardSolverRouteMatching'
import { eModeDialogConfig } from '@/features/dialogs/e-mode/EModeDialog'
import { repayDialogConfig } from '@/features/dialogs/repay/RepayDialog'
import { ApyWithRewardsCell } from '@/features/my-portfolio/components/ApyWithRewardsCell'
import { EModeIndicator } from '@/features/my-portfolio/components/borrow-table/components/EModeIndicator'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { ApyTooltip } from '@/ui/molecules/apy-tooltip/ApyTooltip'
import { ActionsCell } from '@/ui/molecules/data-table/components/ActionsCell'
import { CompactValueCell } from '@/ui/molecules/data-table/components/CompactValueCell'
import { TokenWithLogo } from '@/ui/molecules/data-table/components/TokenWithLogo'
import { ColumnDefinition } from '@/ui/molecules/data-table/types'
import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'
import { useMemo } from 'react'
import { useAccount, useChainId } from 'wagmi'
import { Borrow } from '../../logic/assets'

export interface MyBorrowsTableProps {
  assets: Borrow[]
  eModeCategoryId: EModeCategoryId
  openDialog: OpenDialogFunction
}

export function MyBorrowsTable({ assets, eModeCategoryId, openDialog }: MyBorrowsTableProps) {
  const { address } = useAccount()
  const { getByAddress, error: pointsDataError, isLoading: isPointsDataLoading } = usePointsData()
  const { byAddress: cardSolverConfigsByAddress } = useCardSolverConfigs()
  const isStoreHydrated = useStoreHydrated()
  const hasPointsError = !isPointsDataLoading && !!pointsDataError?.message
  const chainId = useChainId()
  const { pooledPartnerPoints } = getChainConfigEntry(chainId)
  const extraIncentivesUSDC = useExtraIncentivesUSDC()
  const cardSolverConfigs = useMemo(
    () => (isStoreHydrated && address ? Object.values(cardSolverConfigsByAddress[address.toLowerCase()] ?? {}) : []),
    [address, cardSolverConfigsByAddress, isStoreHydrated],
  )
  const columnDefinition = useMemo<{ [key: string]: ColumnDefinition<Borrow> }>(() => {
    return {
      symbol: {
        header: 'Assets',
        renderCell: ({ token, reserveStatus, isCombinedBalance }) => {
          const displayToken = getDisplayToken(chainId, token)
          const cardRouteBadge = getPooledBorrowCardRouteBadge(cardSolverConfigs, token.address)
          return (
            <div className="flex items-center gap-2">
              <TokenWithLogo
                token={displayToken}
                reserveStatus={reserveStatus}
                isCombinedBalance={isCombinedBalance}
                hideName
              />
              <CardRouteBadge badge={cardRouteBadge} />
            </div>
          )
        },
      },
      deposit: {
        header: 'Debt',
        sortable: true,
        sortingFn: (a, b) => sortByUsdValue(a.original, b.original, 'debt'),
        headerAlign: 'right',
        showOnMobile: true,
        renderCell: ({ token, debt }, mobileViewOptions) => (
          <CompactValueCell token={token} value={debt} mobileViewOptions={mobileViewOptions} hideEmpty />
        ),
      },
      apy: {
        header: <ApyTooltip variant="borrow">APY</ApyTooltip>,
        headerAlign: 'right',
        sortable: true,
        showOnMobile: true,
        sortingFn: (a, b) =>
          sortByAPY(
            applyExtraIncentivesUSDCToBorrowAPY(a.original.borrowAPY, a.original.token.symbol, extraIncentivesUSDC)
              .totalAPY,
            applyExtraIncentivesUSDCToBorrowAPY(b.original.borrowAPY, b.original.token.symbol, extraIncentivesUSDC)
              .totalAPY,
          ),
        renderCell: ({ borrowAPY, token }, mobileViewOptions) => {
          const points = getByAddress({
            type: 'pooled',
            action: 'borrow',
            address: token.address,
          })

          const { baseAPY, bonusAPY, totalAPY } = applyExtraIncentivesUSDCToBorrowAPY(
            borrowAPY,
            token.symbol,
            extraIncentivesUSDC,
          )
          const additionalAPY = bonusAPY ? formatPercentage(bonusAPY) : undefined

          return (
            <ApyWithRewardsCell
              action="borrow"
              asset={token.symbol}
              apy={totalAPY}
              points={points}
              partnerPoints={pooledPartnerPoints[CheckedAddress(token.address)]?.points}
              mobileViewOptions={mobileViewOptions}
              pointsError={hasPointsError}
              pointsLoading={isPointsDataLoading}
              extraIncentives={
                token.symbol === 'USDC' && extraIncentivesUSDC ? extraIncentivesUSDC.description : undefined
              }
              baseAPYDisplay={baseAPY ? formatPercentage(baseAPY) : undefined}
              totalAPYDisplay={totalAPY ? formatPercentage(totalAPY) : undefined}
              additionalAPY={additionalAPY}
              showAdditionalAPYInCell={false}
              gradient={!!bonusAPY}
            />
          )
        },
      },
      actions: {
        header: '',
        renderCell: ({ token, debt }) => {
          return (
            <ActionsCell>
              <Button
                variant="outline"
                size="sm"
                className="w-full md:w-fit"
                disabled={debt.isZero()}
                onClick={() => {
                  openDialog(repayDialogConfig, { token })
                }}
              >
                Repay
              </Button>
            </ActionsCell>
          )
        },
      },
    }
  }, [
    openDialog,
    getByAddress,
    hasPointsError,
    isPointsDataLoading,
    pooledPartnerPoints,
    chainId,
    extraIncentivesUSDC,
    cardSolverConfigs,
  ])

  return (
    <Panel
      collapsibleOptions={{ collapsible: true, collapsibleAbove: 'md', fullHeight: true }}
      className="bg-panel xl:h-full md:px-3"
    >
      <Panel.Header>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-1">
          <Panel.Title className="text-xl md:px-3" gradient>
            My borrows
          </Panel.Title>
          <EModeIndicator
            hideInfo={false}
            eModeCategoryId={eModeCategoryId}
            onButtonClick={() => {
              openDialog(eModeDialogConfig, { userEModeCategoryId: eModeCategoryId })
            }}
          />
        </div>
      </Panel.Header>

      <Panel.Content>
        <ResponsiveDataTable
          gridTemplateColumnsClassName="lg:grid-cols-[3fr_repeat(2,_3fr)_4fr] grid-cols-[repeat(3,_3fr)_8fr]"
          columnDefinition={columnDefinition}
          data={assets}
        />
      </Panel.Content>
    </Panel>
  )
}
